/**
 * DataFlow Console API Client
 *
 * API functions for managing DataFlow task chains, instances, templates, and queue.
 * These endpoints interface with the DataFlow task execution system.
 */

import { z } from "zod";

import { apiEnvelopeSchema } from "@/schemas/api/market";
import type { Instance, TaskChain, TaskHandler, Template } from "../../types/dataflow";

const API_BASE = "/api/v2";

function extractErrorMessage(payload: unknown, status: number): string {
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    const data = payload as Record<string, unknown>;
    const detail = data.detail;
    if (typeof detail === "string" && detail.trim()) {
      return detail.trim();
    }
    if (Array.isArray(detail)) {
      const parts = detail
        .map((item) => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object") {
            const msg = (item as Record<string, unknown>).msg;
            return typeof msg === "string" ? msg : "";
          }
          return "";
        })
        .filter(Boolean);
      if (parts.length > 0) return parts.join("; ");
    }
    const error = data.error;
    if (typeof error === "string" && error.trim()) {
      return error.trim();
    }
    const message = data.message;
    if (typeof message === "string" && message.trim()) {
      return message.trim();
    }
  }
  return `Request failed: ${status}`;
}

export function parseDataflowPayload(payload: unknown, responseOk: boolean, status: number) {
  const isEnvelopePayload =
    typeof payload === "object" &&
    payload !== null &&
    !Array.isArray(payload) &&
    Object.prototype.hasOwnProperty.call(payload, "data");

  if (isEnvelopePayload) {
    const parsedEnvelope = apiEnvelopeSchema(z.any()).safeParse(payload);
    if (!parsedEnvelope.success) {
      throw new Error(`Request failed: ${status}`);
    }
    if (!responseOk || parsedEnvelope.data.error) {
      throw new Error(parsedEnvelope.data.error || `Request failed: ${status}`);
    }
    return parsedEnvelope.data.data;
  }

  if (!responseOk) {
    throw new Error(extractErrorMessage(payload, status));
  }

  return payload;
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const startedAt = performance.now();
  const response = await fetch(`${API_BASE}${url}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
    ...init,
  });
  const payload = await response.json();
  const endedAt = performance.now();
  window.dispatchEvent(new CustomEvent("lisaq:api-latency", { detail: Math.round(endedAt - startedAt) }));
  return parseDataflowPayload(payload, response.ok, response.status) as T;
}

export interface TimelineEvent {
  id: string;
  timestamp: string;
  type: "instance.started" | "instance.completed" | "instance.failed" | "instance.cancelled";
  instance_id: number;
  data: Record<string, unknown>;
}

export interface QueueStatus {
  depth: number;
  slots: Array<{
    id: number;
    status: "idle" | "running" | "error" | string;
    instance_id?: number;
    task_type?: string;
    name?: string;
    worker_id?: string;
  }>;
  pending_instances: Array<{
    id: number;
    batch_id?: number;
    template_id: number;
    name: string;
    status: string;
    task_type: string;
    priority: number;
    queued_at?: string;
    started_at?: string;
    worker_id?: string;
    trigger_mode?: string;
  }>;
  running_instances: Array<{
    id: number;
    batch_id?: number;
    template_id: number;
    name: string;
    status: string;
    task_type: string;
    priority: number;
    queued_at?: string;
    started_at?: string;
    worker_id?: string;
    trigger_mode?: string;
  }>;
  updated_at?: string;
}

export interface TimelineResponse {
  events: TimelineEvent[];
}

export interface RetryResponse {
  instance_id: number;
}

export interface TemplateUpsertPayload {
  name: string;
  task_type: string;
  stock_pool_type?: Template["stock_pool_type"];
  stock_pool_params?: Record<string, unknown>;
  date_range_type?: Template["date_range_type"];
  date_range_params?: Record<string, unknown>;
  options_json?: Record<string, unknown>;
  depends_on_template_ids?: number[];
  schedule_kind?: string;
  schedule_config_json?: Template["schedule_config_json"];
  inherit_upstream_scope?: boolean;
}

export const dataflowApi = {
  // Task Chains (backward compatible endpoint: /batches)
  getBatches(params?: { date?: string; limit?: number }): Promise<TaskChain[]> {
    const searchParams = new URLSearchParams();
    if (params?.date) {
      searchParams.set("date", params.date);
    }
    if (params?.limit != null) {
      searchParams.set("limit", String(params.limit));
    }
    const query = searchParams.toString();
    return request<TaskChain[]>(`/dataflow/batches${query ? `?${query}` : ""}`);
  },

  getBatch(id: string): Promise<TaskChain & { instances: Instance[] }> {
    return request<TaskChain & { instances: Instance[] }>(`/dataflow/batches/${id}`);
  },

  getTaskChains(params?: { date?: string; limit?: number }): Promise<TaskChain[]> {
    return dataflowApi.getBatches(params);
  },

  getTaskChain(id: string): Promise<TaskChain & { instances: Instance[] }> {
    return dataflowApi.getBatch(id);
  },

  // Instances
  getInstances(params?: { batch_id?: string; template_id?: string }): Promise<Instance[]> {
    const searchParams = new URLSearchParams();
    if (params?.batch_id) {
      searchParams.set("batch_id", params.batch_id);
    }
    if (params?.template_id) {
      searchParams.set("template_id", params.template_id);
    }
    const query = searchParams.toString();
    return request<Instance[]>(`/dataflow/instances${query ? `?${query}` : ""}`);
  },

  getInstance(id: number): Promise<Instance> {
    return request<Instance>(`/dataflow/instances/${id}`);
  },

  getInstanceLogs(id: number, limit: number = 100): Promise<Array<{ id: number; ts: string; level: string; message: string; step_key?: string; progress_pct?: number }>> {
    return request<Array<{ id: number; ts: string; level: string; message: string; step_key?: string; progress_pct?: number }>>(`/dataflow/instances/${id}/logs?limit=${limit}`);
  },

  cancelInstance(id: number): Promise<void> {
    return request<void>(`/dataflow/instances/${id}/cancel`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },

  retryInstance(id: number): Promise<RetryResponse> {
    return request<RetryResponse>(`/dataflow/instances/${id}/retry`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },

  // Templates
  getTemplates(): Promise<Template[]> {
    return request<Template[]>("/dataflow/templates");
  },

  getTemplate(id: string): Promise<Template> {
    return request<Template>(`/dataflow/templates/${id}`);
  },

  createTemplate(data: TemplateUpsertPayload): Promise<Template> {
    return request<Template>("/dataflow/templates", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  updateTemplate(id: string, data: TemplateUpsertPayload): Promise<void> {
    return request<void>(`/dataflow/templates/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  deleteTemplate(id: string): Promise<void> {
    return request<void>(`/dataflow/templates/${id}`, {
      method: "DELETE",
    });
  },

  runTemplate(id: string): Promise<RetryResponse> {
    return request<RetryResponse>(`/dataflow/templates/${id}/run`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },

  // Queue
  getQueue(): Promise<QueueStatus> {
    return request<QueueStatus>("/dataflow/queue");
  },

  // Timeline
  getTimeline(params?: { limit?: number; since?: string }): Promise<TimelineResponse> {
    const searchParams = new URLSearchParams();
    if (params?.limit != null) {
      searchParams.set("limit", String(params.limit));
    }
    if (params?.since) {
      searchParams.set("since", params.since);
    }
    const query = searchParams.toString();
    return request<TimelineResponse>(`/dataflow/timeline${query ? `?${query}` : ""}`);
  },

  // Task Handlers
  getHandlers(): Promise<TaskHandler[]> {
    return request<TaskHandler[]>("/dataflow/handlers");
  },
};
